import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import styles from './VLAOperatorDetail.module.css';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../configs/config';

const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const formatPoints = (n) => Number(n || 0).toLocaleString('en-US');

const getAdminToken = () => localStorage.getItem('prismax_admin_token');

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        });
    };
    return (
        <button className={styles.copyButton} onClick={handleCopy} title="Copy">
            {copied
                ? <Check size={13} className={styles.copyIconDone} />
                : <Copy size={13} className={styles.copyIcon} />}
        </button>
    );
};

const VLAAdminOperatorDetail = ({ userId, email, app, onClose, onDeny, onOpenDashboard }) => {
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    const [keys, setKeys] = useState([]);
    const [keysLoading, setKeysLoading] = useState(false);
    const [keysError, setKeysError] = useState(null);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createLabel, setCreateLabel] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);

    const [revealSecret, setRevealSecret] = useState(null);

    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revoking, setRevoking] = useState(false);

    const [denying, setDenying] = useState(false);

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res = await fetch(
                `${PRISMAX_DATA_PIPELINE_URL}/data/admin/operator-detail/stats?user_id=${userId}`,
                { headers: { 'Authorization': `Bearer ${getAdminToken()}` } }
            );
            const result = await res.json();
            if (result.success) setStats(result.data);
        } catch (err) {}
        finally { setStatsLoading(false); }
    }, [userId]);

    const fetchKeys = useCallback(async () => {
        setKeysLoading(true);
        setKeysError(null);
        try {
            const res = await fetch(
                `${PRISMAX_DATA_PIPELINE_URL}/data/admin/upload-api-keys?user_id=${userId}`,
                { headers: { 'Authorization': `Bearer ${getAdminToken()}` } }
            );
            const result = await res.json();
            if (result.success) {
                setKeys(result.data);
            } else {
                setKeysError(result.msg || 'Failed to load upload keys.');
            }
        } catch (err) {
            setKeysError('Failed to load upload keys.');
        } finally {
            setKeysLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchStats();
        fetchKeys();
    }, [fetchStats, fetchKeys]);

    const activeKeyCount = keys.filter((k) => k.status === 'ACTIVE').length;

    const handleOpenDashboardClick = () => {
        if (onOpenDashboard) onOpenDashboard(userId, email || app?.email);
    };

    const handleDenyClick = async () => {
        if (!onDeny) return;
        setDenying(true);
        try {
            const success = await onDeny();
            if (success) onClose();
        } finally {
            setDenying(false);
        }
    };

    const handleCreateKey = async () => {
        setCreating(true);
        setCreateError(null);
        try {
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/upload-api-keys`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getAdminToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: userId, label: createLabel.trim() }),
            });
            const result = await res.json();
            if (result.success) {
                setCreateModalOpen(false);
                setCreateLabel('');
                setRevealSecret(result.data.api_key);
                fetchKeys();
            } else {
                setCreateError(result.msg || 'Failed to create upload key.');
            }
        } catch (err) {
            setCreateError('Failed to create upload key.');
        } finally {
            setCreating(false);
        }
    };

    const handleRevokeKey = async () => {
        if (!revokeTarget) return;
        setRevoking(true);
        try {
            const res = await fetch(
                `${PRISMAX_DATA_PIPELINE_URL}/data/admin/upload-api-keys/${revokeTarget.upload_api_key_id}?user_id=${userId}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${getAdminToken()}` },
                }
            );
            const result = await res.json();
            if (result.success) {
                setRevokeTarget(null);
                fetchKeys();
            }
        } catch (err) {}
        finally { setRevoking(false); }
    };

    const robots = app?.robots || [];

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <button className={styles.back} onClick={onClose}>
                    ← Back to approved operators
                </button>

                <div className={styles.opHead}>
                    <div>
                        <h2 className={styles.title}>{email || app?.email || '—'}</h2>
                        <div className={styles.metaRow}>
                            <span className={styles.mono}>ID {userId}</span>
                            <span className={styles.dot}>•</span>
                            <span className={styles.approvedPill}>Approved</span>
                            {app?.user_class && (
                                <span className={`${styles.classPill} ${styles[`class_${app.user_class}`] || ''}`}>
                                    {app.user_class}
                                </span>
                            )}
                            {app?.total_points != null && (
                                <>
                                    <span className={styles.dot}>•</span>
                                    <span>{formatPoints(app.total_points)} pts</span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className={styles.headActs}>
                        <button className={styles.btnBlue} onClick={handleOpenDashboardClick}>
                            Open dashboard
                        </button>
                        <button className={styles.btnRed} onClick={handleDenyClick} disabled={denying}>
                            {denying ? 'Denying…' : 'Deny operator'}
                        </button>
                    </div>
                </div>

                <div className={styles.body}>
                    <div className={styles.statGrid}>
                        <div className={styles.stat}>
                            <div className={styles.statKey}>Approved on</div>
                            <div className={styles.statValueSmall}>{formatDate(app?.requested_at)}</div>
                        </div>
                        <div className={styles.stat}>
                            <div className={styles.statKey}>Episodes uploaded</div>
                            <div className={styles.statValue}>
                                {statsLoading ? '—' : formatPoints(stats?.total_episodes ?? 0)}
                            </div>
                        </div>
                        <div className={styles.stat}>
                            <div className={styles.statKey}>Last upload</div>
                            <div className={styles.statValueSmall}>
                                {statsLoading ? '—' : formatDate(stats?.last_upload_at)}
                            </div>
                        </div>
                        <div className={styles.stat}>
                            <div className={styles.statKey}>Active upload keys</div>
                            <div className={styles.statValue}>{activeKeyCount}</div>
                        </div>
                    </div>

                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Registered robots</h3>
                        <div className={styles.card}>
                            {robots.length === 0 ? (
                                <div className={styles.empty}>No registered robots.</div>
                            ) : (
                                robots.map((r) => (
                                    <div key={r.machine_id} className={styles.robotRow}>
                                        <div>
                                            <div>{r.manufacturer} {r.model}</div>
                                            <div className={styles.robotSerial}>{r.serial_number}</div>
                                        </div>
                                        <span className={styles.pairedPill}>Paired</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className={styles.section}>
                        <div className={styles.sectionHeadRow}>
                            <div>
                                <h3 className={styles.sectionTitle}>Upload API keys</h3>
                                <p className={styles.sectionSub}>
                                    Keys this operator uses to push episodes from their capture machine.
                                    The full key is shown once at creation — after that only the prefix is stored.
                                </p>
                            </div>
                            <button
                                className={styles.btnSolid}
                                onClick={() => { setCreateError(null); setCreateModalOpen(true); }}
                            >
                                Create upload key
                            </button>
                        </div>

                        <div className={styles.card}>
                            {keysLoading && <div className={styles.empty}>Loading…</div>}
                            {!keysLoading && keysError && <div className={styles.errorMsg}>{keysError}</div>}
                            {!keysLoading && !keysError && keys.length === 0 && (
                                <div className={styles.empty}>
                                    <div className={styles.emptyTitle}>No upload keys yet</div>
                                    This operator can't push episodes until you create one.
                                </div>
                            )}
                            {!keysLoading && !keysError && keys.length > 0 && (
                                <>
                                    <div className={styles.keyHead}>
                                        <div>Label</div>
                                        <div>Key</div>
                                        <div>Created</div>
                                        <div>Last used</div>
                                        <div>Status</div>
                                    </div>
                                    {keys.map((k) => (
                                        <div
                                            key={k.upload_api_key_id}
                                            className={`${styles.keyRow} ${k.status === 'REVOKED' ? styles.keyRowGone : ''}`}
                                        >
                                            <div>{k.label || '—'}</div>
                                            <div className={styles.mono}>{k.key_prefix}••••••••••••••••••</div>
                                            <div className={styles.dim}>{formatDate(k.created_at)}</div>
                                            <div className={styles.dim}>{k.last_used_at ? formatDate(k.last_used_at) : 'Never'}</div>
                                            <div className={styles.statusActions}>
                                                <span className={k.status === 'ACTIVE' ? styles.activePill : styles.revokedPill}>
                                                    {k.status === 'ACTIVE' ? 'Active' : 'Revoked'}
                                                </span>
                                                {k.status === 'ACTIVE' && (
                                                    <button
                                                        className={styles.btnRedSm}
                                                        onClick={() => setRevokeTarget(k)}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {createModalOpen && (
                <div className={styles.innerOverlay} onClick={() => !creating && setCreateModalOpen(false)}>
                    <div className={styles.innerModal} onClick={(e) => e.stopPropagation()}>
                        <h4 className={styles.innerTitle}>Create upload key</h4>
                        <p className={styles.innerSub}>For {email || app?.email}</p>
                        <label className={styles.formLabel} htmlFor="opd-key-label">Label</label>
                        <input
                            id="opd-key-label"
                            type="text"
                            className={styles.formInput}
                            placeholder="e.g. Bench station A"
                            value={createLabel}
                            onChange={(e) => setCreateLabel(e.target.value)}
                            autoFocus
                        />
                        {createError && <div className={styles.errorMsg}>{createError}</div>}
                        <div className={styles.innerActions}>
                            <button
                                className={styles.btn}
                                onClick={() => setCreateModalOpen(false)}
                                disabled={creating}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.btnSolid}
                                onClick={handleCreateKey}
                                disabled={creating}
                            >
                                {creating ? 'Creating…' : 'Create key'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {revealSecret && (
                <div className={styles.innerOverlay} onClick={() => setRevealSecret(null)}>
                    <div className={styles.innerModal} onClick={(e) => e.stopPropagation()}>
                        <h4 className={styles.innerTitle}>Key created</h4>
                        <p className={styles.innerSub}>Copy it now — you won't be able to see it again.</p>
                        <div className={styles.reveal}>
                            <code className={styles.revealCode}>{revealSecret}</code>
                            <CopyButton text={revealSecret} />
                        </div>
                        <div className={styles.warnNote}>
                            Send this to the operator over a channel you trust. If it leaks, delete the key here and issue a new one.
                        </div>
                        <div className={styles.innerActions}>
                            <button className={styles.btnSolid} onClick={() => setRevealSecret(null)}>
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {revokeTarget && (
                <div className={styles.innerOverlay} onClick={() => !revoking && setRevokeTarget(null)}>
                    <div className={styles.innerModal} onClick={(e) => e.stopPropagation()}>
                        <h4 className={styles.innerTitle}>Delete "{revokeTarget.label || revokeTarget.key_prefix}"?</h4>
                        <p className={styles.innerSub}>Key {revokeTarget.key_prefix}••••••••••</p>
                        <div className={styles.dangerNote}>
                            Uploads using this key start failing immediately. Episodes already uploaded are not affected. This can't be undone.
                        </div>
                        <div className={styles.innerActions}>
                            <button
                                className={styles.btn}
                                onClick={() => setRevokeTarget(null)}
                                disabled={revoking}
                            >
                                Keep key
                            </button>
                            <button
                                className={styles.btnDangerSolid}
                                onClick={handleRevokeKey}
                                disabled={revoking}
                            >
                                {revoking ? 'Deleting…' : 'Delete key'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VLAAdminOperatorDetail;
